import { useEffect, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Dropzone as DropzoneUi, FileCard as File } from '@files-ui/react';
import { Box, Typography, Link } from '@mui/material';
import UploadFile from '@mui/icons-material/Backup';
import { usePrevious } from '../hooks/use-previous';

Dropzone.propTypes = {
  maxFileSize: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  fileType: PropTypes.string,
  contentType: PropTypes.string,
  isUploading: PropTypes.bool,
};

export function Dropzone(props) {
  const {
    contentType,
    maxFileSize = 1024 * 1024 * 750,
    fileType,
    onChange,
    isUploading,
    renderPreview,
    ...passThroughProps
  } = props;
  const previouslyUploading = usePrevious(isUploading);
  const [files, setFiles] = useState([]);
  const fileContents = contentType ? ` of ${contentType}` : '';
  const instructions = `Drag and drop a ${fileType} file ${fileContents} or click`;

  const updateFiles = useCallback(
    (dropped) => {
      setFiles(dropped);
      onChange(dropped.filter((file) => file.valid).map((file) => file.file));
    },
    [onChange],
  );

  const removeFile = useCallback(
    (id) => {
      updateFiles(files.filter((file) => file.id !== id));
    },
    [files, updateFiles],
  );

  useEffect(() => {
    if (!isUploading && previouslyUploading) {
      updateFiles([]);
    }
  }, [updateFiles, isUploading, previouslyUploading]);

  return (
    <DropzoneUi
      value={files}
      maxFileSize={maxFileSize}
      onChange={updateFiles}
      accept={`.${fileType}`}
      minHeight={250}
      color="#333333"
      background="#f0f0f0"
      style={{ fontFamily: 'Roboto,Helvetica Neue,Arial,sans-serif' }}
      headerConfig={{ customHeader: <Header {...props} files={files} /> }}
      footer={false}
      {...passThroughProps}
    >
      {buildContent()}
    </DropzoneUi>
  );

  function buildContent() {
    if (renderPreview) return renderPreview(files);

    return files.length ? buildFilesPreview() : buildInstructions();
  }

  function buildFilesPreview() {
    return files.map((file, i) => (
      <File key={i} {...file} onDelete={removeFile} style={{ margin: 16 }} />
    ));
  }

  function buildInstructions() {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Typography variant="h5">{instructions}</Typography>
        <UploadFile sx={{ mt: 2, fontSize: 48 }} />
      </Box>
    );
  }
}

function Header(props) {
  const { files, maxFileSize } = props;
  const tooBig = useMemo(
    () => files.some((file) => file.size > maxFileSize),
    [files, maxFileSize],
  );

  if (!tooBig) return null;

  return (
    <Box sx={{ p: 1, color: 'error' }}>
      <Typography>
        {`The maximum file size is ${maxFileSize / 1024 / 1024} MB. `}
        <Link
          href="/ResizingZipFiles.pdf"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
        >
          Need help resizing large files?
        </Link>
      </Typography>
    </Box>
  );
}
